<!--
  - Copyright (c) 2022.
  -
  - Unless required by applicable law or agreed to in writing, software
  - distributed under the License is distributed on an "AS IS" BASIS,
  - WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  - See the License for the specific language governing permissions and
  - limitations under the License.
  -
  -->

<template>
    <div>
        <van-nav-bar
            :title="appointment.id?'查看预约':'添加预约'"
            left-arrow
            @click-left="$router.go(-1)"
        />

        <van-notice-bar text="仅支持预约3天后的体检，预约成功后会发送确认短信给受检人。"/>

        <van-steps :active="currentStep" active-icon="success" active-color="var(--color)">
            <van-step>预约体检</van-step>
            <van-step>健康问卷</van-step>
            <van-step>确认预约</van-step>
            <van-step>检前须知</van-step>
            <van-step>体检完成</van-step>
        </van-steps>

        <van-form label-width="100" style="background: white;">

            <van-field
                readonly
                v-show="appointment.id>0"
                :value="appointment.id"
            >
                <template #input>
                    <span>{{ appointment.id }}</span>&nbsp;<font-awesome-icon icon="copy" style="color: var(--color);"
                                                                              @click="copyAppointmentIdClicked"/>
                </template>
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}">预约单号</div>
                </template>
            </van-field>

            <van-field
                readonly
                :clickable="appointment.id===0"
                :value="appointment.productName"
                placeholder="点击选择体检订单"
                @click="orderSelectorClicked"
            >
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}"><span style="color: red;">* </span><span>体检项目</span></div>
                </template>
            </van-field>

            <van-field label="附加项目" v-show="appointment.productOrder.orderAdds">
                <template #input>
                    <div v-for="add in appointment.productOrder.orderAdds">{{ add.name }}</div>
                </template>
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}">附加项目</div>
                </template>
            </van-field>

            <van-field label="体检地址">
                <template #input>
                    <div style=""><span style="font-weight: 600;">平安检测中心上海分中心</span><br/>上海市杨浦区国霞路298号INNO创智B座 <a
                        target="_blank"
                        href="https://apis.map.qq.com/uri/v1/marker?marker=coord:31.314899,121.506042;title:平安检测中心上海分中心;addr:上海市杨浦区国霞路298号INNO创智B座&referer=wubaian">
                        <font-awesome-icon icon="map" style="color: var(--color);"/>
                    </a></div>
                </template>
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}">体检地址</div>
                </template>
            </van-field>

            <van-field
                readonly
                :clickable="appointment.id===0"
                :value="getAppointmentTesterDesc()"
                placeholder="点击选择受检人"
                @click="testerSelectorClicked"
            >
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}"><span style="color: red;">* </span><span>受检人</span></div>
                </template>
            </van-field>

            <van-field
                readonly
                :clickable="appointment.id===0 || appointment.status === 1"
                :value="(appointment.testDateString?appointment.testDateString:'')+changeDateString"
                placeholder="点击选择日期"
                @click="showCalendarClicked"
            >
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0 && appointment.status !==1}"><span style="color: red;">* </span><span>体检日期</span></div>
                </template>
            </van-field>
            <van-calendar :formatter="formatter" :show-confirm="false" :min-date="minDate" :max-date="maxDate"
                          v-model="showCalendar" @confirm="testDateSelected"/>

            <van-field
                readonly
                :clickable="appointment.id===0 || appointment.status === 1"
                :value="(appointment.testTimeString?appointment.testTimeString:'')+changeDateString"
                placeholder="点击选择时间"
                @click="showTimePickerClicked"
            >
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0 && appointment.status !==1}"><span style="color: red;">* </span><span>体检时间</span></div>
                </template>
            </van-field>

            <van-popup v-model="showTimePicker" position="bottom">
                <van-datetime-picker
                    type="time"
                    :filter="filter"
                    :min-hour="8"
                    :max-hour="15"
                    @confirm="testTimeSelected"
                    @cancel="showTimePicker = false"
                />
            </van-popup>

            <van-field
                v-show="false"
                :readonly="appointment.id>0"
            />

            <van-field v-show="appointment.id>0"
                       readonly
                       :value="appointment.testDateString"
            >

                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}">预约状态</div>
                </template>
                <template #input>
                    <van-tag size="large" :color="appointment.statusColor">{{ appointment.statusName }}</van-tag>
                </template>
            </van-field>

            <van-field v-show="appointment.id>0"
                       readonly
                       :value="appointment.testDateString"
            >
                <template #label>
                    <div :class="{'label-disabled': appointment.id>0}">健康问卷</div>
                </template>
                <template #input>
                    <van-tag size="large" :color="appointment.surveyDate?'#0CC160':'#FF976A'">
                        {{ appointment.surveyDate ? '已填写' : '未填写' }}
                    </van-tag>
                </template>
                <template v-if="appointment.surveyCode" #button>
                    <van-button size="small" type="primary" @click="$router.push('/s?c='+appointment.surveyCode)">{{ appointment.surveyDate ? '重新' : '' }}填写健康问卷</van-button>
                </template>
            </van-field>

            <div style="padding: 16px;">
                <van-button v-if="!appointment.id" :loading="loading" round block color="var(--color)"
                            @click="saveAppointmentClicked">提交预约
                </van-button>
                <van-button :disabled="shouldDisableUpdateDateButton()" style="margin-bottom: 10px;" v-if="appointment.id && appointment.status === 1" :loading="loading" round block
                            type="info" @click="saveAppointmentDateClicked">修改预约时间
                </van-button>
                <van-button v-if="appointment.id && appointment.status === 1" :loading="loading" round block
                            type="danger" @click="cancelAppointmentClicked">取消预约
                </van-button>

                <div v-if="appointment.id>0 && appointment.status!==1" style="color: #1A89FA;" @click="serviceOnlineClicked">如需修改预约，可点此联系客服</div>
            </div>
        </van-form>

        <!--        选择订单-->
        <van-popup closeable v-model="showOrderSelector" position="bottom">

            <div style="text-align: center;padding: 15px 0; font-weight: 600;">选择订单</div>

            <van-card style="background: white;" @click="orderSelected(order)" :num="order.num"
                      v-for="order in orders"
            >
                <template #tags>
                    <van-tag :color="order.stateColor">{{ order.stateName }}</van-tag>
                </template>
                <template #title>
                    <div style="font-size: 16px;">{{ order.product.name }}</div>
                </template>
                <template #price>
                    <div style="color: var(--color); font-size: 16px;">￥{{ (order.priceInCent / 100).toFixed(2) }}</div>
                </template>
                <template #thumb>
                    <van-image :src="baseUrl+'/api/public/product_'+order.productId+'.jpg'"/>
                </template>
                <template #desc>
                    <div>{{ order.summary }}</div>
                </template>
            </van-card>
        </van-popup>

        <!--        选择受检人-->
        <van-popup closeable v-model="showTesterSelector" position="bottom">
            <div style="text-align: center;padding: 15px 0; font-weight: 600;">选择受检人</div>
            <van-cell-group>
                <van-cell center :title="tester.name" :value="tester.mobile" @click="testerSelected(tester)"
                          v-for="tester in testers">
                    <template #label>
                        <span>{{ tester.sexName }} {{ tester.ageName }}</span>
                    </template>
                </van-cell>
            </van-cell-group>

            <van-button color="var(--color)" block @click="$router.push('/my_tester_edit')">增加新受检人</van-button>
        </van-popup>
    </div>
</template>

<script>
import moment from "moment";
import http, {BASE_URL, SERVICE_TEL} from "@/utils/http";
import qs from "qs";
import {Dialog, Notify} from "vant";

export default {
    name: "MyAppointmentEdit",
    data() {
        return {
            baseUrl: BASE_URL,
            loading: false,
            appointment: {id: 0, tester: {}, productOrder: ''},
            currentStep: -1,

            orders: [],
            testers: [],

            showCalendar: false,
            showTimePicker: false,
            minDate: moment().add(4, 'days').toDate(),
            maxDate: moment().add(30, 'days').toDate(),
            showOrderSelector: false,
            showTesterSelector: false,
            changeDateString: '',
            lastDateTimeString: ''
        };
    },
    components: {},
    mounted() {
        var id = this.$route.query.id ? this.$route.query.id : '';
        if (!id) {
            id = '0';
        }
        this.appointment.id = parseInt(id);
        if (this.appointment.id) {
            this.loadDetail()
        }

        this.loadProductOrders();
        this.loadTesters();

        // this.$copyText(this.message)
        //如果需要回调：
        // this.$copyText("this.message").then( e => {
        //     alert('复制成功')
        //     console.log(e)
        // }, function (e) {
        //     alert('复制失败')
        //     console.log(e)
        // })

    },
    methods: {
        shouldDisableUpdateDateButton() {
            if(this.appointment.status !== 1) {
                return false;
            }
            var dateTimeString = this.appointment.testDate+' '+this.appointment.testTime;
            return dateTimeString === this.lastDateTimeString;
        },
        loadDetail: function () {
            http.get('/api/personal/load_appointment?id=' + this.appointment.id).then(res => {
                // console.log(res)
                if (!res.data.result) {
                    Notify({type: 'danger', message: res.data.message});
                } else {
                    var appointment = res.data.extra.appointment;
                    appointment.tester = JSON.parse(appointment.testerDesc);
                    var testDate = moment(appointment.testDate);
                    appointment.testDateString = testDate.format("YYYY年M月D日") + ' 星期' + '日一二三四五六'.charAt(testDate.day());
                    appointment.testDate = testDate.format('YYYY-MM-DD');
                    appointment.testTimeString = testDate.format('HH:mm');
                    appointment.testTime = testDate.format('HH:mm');
                    if(appointment.status === 1) {
                        this.changeDateString = ' (点此可修改)';
                    }
                    this.lastDateTimeString = appointment.testDate+' '+appointment.testTime;

                    var statusColor = '#FF976A';
                    var statusName = '待确认';
                    switch (appointment.status) {
                        case 0:
                            this.currentStep = 4;//step已完成
                            statusColor = '#1A89FA';
                            statusName = '已完成';
                            break;
                        case 1:
                            this.currentStep = 0;
                            if (appointment.surveyDate) {
                                this.currentStep = 1;//step健康问卷
                            }
                            statusColor = '#FF976A';
                            statusName = '待确认';
                            break;
                        case 2:
                            this.currentStep = 2;
                            statusColor = '#0CC160';
                            statusName = '已确认';
                            break;
                    }
                    appointment.statusColor = statusColor;
                    appointment.statusName = statusName;

                    // console.log(appointment)
                    this.appointment = appointment;
                }
            });
        },
        formatter(day) {
            var m = moment(day.date);
            if (moment(day.date).isSame(moment(), 'day')) {
                day.text = '今天';
                day.type = 'disabled';
            }
            // if(moment(day.date).format('YYYY-M-D')===moment().format('YYYY-M-D')){
            //     day.text = '今天';
            // }
            // 周一和周日不能预约
            if (m.day() === 0 || m.day() === 1) { //0-6 星期天是0 星期六是6
                day.type = 'disabled';
            }
            return day;
        },
        getAppointmentTesterDesc() {
            if (this.appointment.tester.name) {
                return this.appointment.tester.name + ' ' + this.appointment.tester.mobile;
            }
            return '';
        },
        filter(type, options) {
            if (type === 'minute') {
                return options.filter((option) => option % 15 === 0);
            }
            return options;
        },
        testDateSelected(date) {
            var testDate = moment(date);
            var testDateString = testDate.format("YYYY年M月D日") + ' 星期' + '日一二三四五六'.charAt(testDate.day());
            this.appointment.testDateString = testDateString;
            this.appointment.testDate = testDate.format('YYYY-MM-DD');
            this.showCalendar = false;
        },
        testTimeSelected(time) {
            this.appointment.testTimeString = time;
            this.appointment.testTime = time;
            this.showTimePicker = false;
        },
        loadProductOrders() {
            http.get('/api/personal/list_unused_orders').then(res => {
                this.orders = res.data;

                if(res.data.length === 1 && this.appointment.id === 0) {//只有一个受检人且是新增的情况下默认填上去
                    this.orderSelected(res.data[0]);
                }
            });
        },
        loadTesters() {
            http.get('/api/personal/list_user_testers').then(res => {
                var nowYear = moment().year();
                var items = res.data;
                items.forEach(itm => {
                    itm.sexName = itm.sex === '1' ? '男' : '女';
                    itm.ageName = (nowYear - moment(itm.birthday).year()) + '岁';
                })
                this.testers = items;

                if(items.length === 1 && this.appointment.id === 0) {//只有一个受检人且是新增的情况下默认填上去
                    this.testerSelected(items[0]);
                }
            });
        },
        orderSelectorClicked() {
            if (this.appointment.id) {
                return;
            }
            if (this.orders.length === 0) {
                Dialog.confirm({
                    confirmButtonText: '购买',
                    message: '<div style="font-size: 16px;">您还没有购买体检套餐（或您的体检套餐已使用完），是否购买？</div>',
                }).then(() => {
                    this.$router.push("/products")
                })
                return;
            }
            this.showOrderSelector = true;
        },
        testerSelectorClicked() {
            if (this.appointment.id) {
                return;
            }
            if (this.testers.length === 0) {
                this.$router.push('/my_tester_edit')
                return;
            }
            this.showTesterSelector = true;
        },
        showCalendarClicked() {
            if (this.appointment.id && this.appointment.status !== 1) {
                return;
            }
            this.showCalendar = true;
        },
        showTimePickerClicked() {
            if (this.appointment.id && this.appointment.status !== 1) {
                return;
            }
            this.showTimePicker = true;
        },
        orderSelected(order) {
            // console.log(order)
            this.appointment.productOrderId = order.id;
            this.appointment.productName = order.product.name;
            order.orderAdds = order.adds ? JSON.parse(order.adds) : [];
            this.appointment.productOrder = order;
            this.showOrderSelector = false;
        },
        testerSelected(tester) {
            this.appointment.testerId = tester.id;
            this.appointment.tester = tester;
            this.showTesterSelector = false;
        },
        copyAppointmentIdClicked() {
            if (!this.appointment.id) {
                return;
            }
            this.$copyText(this.appointment.id + '').then(e => {
                Notify({type: 'success', message: '复制成功'});
            }, function (e) {
            });
        },
        saveAppointmentDateClicked() {
            let payload = JSON.parse(JSON.stringify(this.appointment));
            if (!payload.testDate || !payload.testTime) {
                Notify({type: 'danger', message: '请选择预约日期和时间'});
                return;
            }
            payload.testDate = payload.testDate + ' ' + payload.testTime;
            // console.log(payload)
            this.loading = true;
            http.post('/api/personal/save_appointment', qs.stringify(payload)).then(res => {
                this.loading = false;
                this.lastDateTimeString = payload.testDate;
                if (!res.data.result) {
                    Notify({type: 'danger', message: res.data.message});
                } else {
                    Notify({type: 'success', message: res.data.message});
                }
            }).catch(() => this.loading = false);
        },
        saveAppointmentClicked() {
            let payload = JSON.parse(JSON.stringify(this.appointment));
            if (!payload.testDate || !payload.testTime) {
                Notify({type: 'danger', message: '请选择预约日期和时间'});
                return;
            }
            payload.testDate = payload.testDate + ' ' + payload.testTime;
            // console.log(payload)
            this.loading = true;
            http.post('/api/personal/save_appointment', qs.stringify(payload)).then(res => {
                this.loading = false;
                if (!res.data.result) {
                    Notify({type: 'danger', message: res.data.message});
                } else {
                    Notify({type: 'success', message: res.data.message});
                    if(res.data.extra.confirmSurvey) {
                        Dialog.confirm({
                            title: '健康调查',
                            message: res.data.extra.confirmSurvey,
                        })
                            .then(() => {
                                this.$router.replace('/s?c='+res.data.extra.surveyCode)
                            })
                            .catch(() => {
                                this.$router.go(-1);
                            });
                    } else {
                        this.$router.go(-1);
                    }
                }
            }).catch(() => this.loading = false);
        },
        cancelAppointmentClicked() {
            Dialog.confirm({
                title: '确认',
                message: '确认要取消预约吗',
            })
                .then(() => {
                    this.loading = true;
                    http.get('/api/personal/delete_appointment?id=' + this.appointment.id).then(res => {
                        this.loading = false;
                        if (!res.data.result) {
                            Notify({type: 'danger', message: res.data.message});
                        } else {
                            Notify({type: 'success', message: res.data.message});
                            this.$router.go(-1);
                        }
                    }).catch(() => this.loading = false);
                })
                .catch(() => {
                    // on cancel
                });
        },

        serviceOnlineClicked: function () {
            Dialog.confirm({
                confirmButtonColor: '#b28303',
                confirmButtonText:'拨打',
                message: '<div style="font-size: 16px;">拨打'+SERVICE_TEL+'客服电话</div><div style="color:#A1A1A1;margin-top: 10px;">服务时间：工作日9:30-17:30</div>',
            }).then(() => {
                window.open("tel:"+SERVICE_TEL,'_self');
            })
        },
    },
}
</script>

<style scoped>
    .label-disabled {
        color: #B3B4B5;
    }
    .label-enabled {
        color: #646566;
    }
</style>